import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, BACKEND } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { StatusBadge } from "../components/StatusBadge";
import { FileUpload } from "../components/FileUpload";
import { money, fmtDateAU, DEMO_MONTH } from "../lib/format";
import { Button } from "../components/ui/button";
import { FileSearch, FileText } from "lucide-react";
import { toast } from "sonner";

function Info({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900 text-right">{value}</span>
    </div>
  );
}

export default function PracticeDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["practice", id, DEMO_MONTH],
    queryFn: async () => (await api.get(`/practices/${id}?month=${DEMO_MONTH}`)).data,
  });

  if (isLoading || !data) return <AppShell><PageHeader title="Practice" onBack={() => navigate(-1)} /></AppShell>;
  const p = data.practice;
  if (!p) return (
    <AppShell>
      <PageHeader title="Practice" onBack={() => navigate(-1)} />
      <div className="px-6 py-12 text-center text-sm text-slate-500" data-testid="practice-unavailable">
        Full practice details are unavailable offline. Reconnect to view them.
      </div>
    </AppShell>
  );
  const dash = (v) => v || "—";
  const ca = data.current_agreement || {};
  // Explicit direction for practice-retained agreements (adjusted_billing / practice_share):
  // the configured % is what the PRACTICE keeps; the dentist receives the remainder.
  const practicePct = ca.calc_method === "adjusted_billing"
    && (ca.service_fee_basis || "practice_share") === "practice_share"
    && ca.service_fee_bps != null ? ca.service_fee_bps / 100 : null;

  const saveDoc = async (f) => {
    await api.put(`/practices/${id}/document`, {
      storage_path: f.storage_path, original_filename: f.original_filename, content_type: f.content_type,
    });
    qc.invalidateQueries();
    toast.success("Agreement document saved");
  };
  const viewDoc = () => window.open(`${BACKEND}/api/files/${p.document.storage_path}`, "_blank");

  return (
    <AppShell>
      <PageHeader title={p.name} subtitle={p.suburb} onBack={() => navigate(-1)} />
      <div className="px-5 df-fade-in space-y-4" style={{ paddingBottom: "calc(6.5rem + env(safe-area-inset-bottom))" }}>
        {data._partial ? (
          <div className="df-card p-3 text-xs text-amber-700 bg-amber-50" data-testid="practice-partial-notice">
            Showing offline details — some agreement information updates when you reconnect.
          </div>
        ) : null}
        <div className="df-card p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-3">
            <div className="flex items-baseline justify-between sm:block min-w-0">
              <div className="text-[11px] text-slate-400 shrink-0">Earned</div>
              <div className="font-num font-semibold text-slate-900 text-right sm:text-left break-words tabular-nums">{money(data.expected_cents)}</div>
            </div>
            <div className="flex items-baseline justify-between sm:block min-w-0">
              <div className="text-[11px] text-slate-400 shrink-0">Received</div>
              <div className="font-num font-semibold text-emerald-700 text-right sm:text-left break-words tabular-nums">{money(data.received_cents)}</div>
            </div>
            <div className="flex items-baseline justify-between sm:block min-w-0">
              <div className="text-[11px] text-slate-400 shrink-0">Outstanding</div>
              <div className="font-num font-semibold text-indigo-700 text-right sm:text-left break-words tabular-nums">{money(data.outstanding_cents)}</div>
            </div>
          </div>
        </div>

        <div className="df-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-2">Agreement{data.current_agreement?.version ? ` (v${data.current_agreement.version})` : ""}</div>
          <Info label="Arrangement" value={dash(data.engagement_label)} />
          {practicePct != null ? (
            <>
              <Info label="Payment calculation" value="Practice retains a % of billings" />
              <Info label="Practice retains" value={`${practicePct}%`} />
              <Info label="You receive" value={`${100 - practicePct}%`} />
            </>
          ) : (
            <Info label="Calculation" value={data.method_label ? `${data.method_label} · ${dash(data.rate_label)}` : "Unavailable offline"} />
          )}
          <Info label="Lab fees" value={dash(data.lab_label)} />
          <Info label="Payment frequency" value={dash(data.freq_label)} />
          <Info label="Super" value={dash(data.super_label)} />
          <Info label="GST" value={dash(data.gst_label)} />
          <Info label="Working days" value={data.working_days_labels?.join(", ") || "—"} />
        </div>

        <div className="df-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-2">Agreement Document</div>
          {p.document ? (
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={18} className="text-[#0D786A] shrink-0" />
                <span className="text-sm text-slate-700 truncate">{p.document.original_filename}</span>
              </div>
              <button data-testid="view-document" onClick={viewDoc} className="text-sm font-medium text-[#0D786A] df-pressable">View</button>
            </div>
          ) : (
            <p className="text-sm text-slate-500 mb-3">Attach a copy of your agreement for safe keeping.</p>
          )}
          <FileUpload testId="upload-document" label={p.document ? "Replace document" : "Upload Agreement"} onUploaded={saveDoc} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => navigate(`/practices/${id}/agreement`)}
            data-testid="detail-edit-agreement" className="w-full min-w-0 h-11 rounded-xl text-sm px-2">Edit Agreement</Button>
          <Button onClick={() => navigate(`/practices/${id}/check?start=${DEMO_MONTH}-01&end=${DEMO_MONTH}-30`)}
            data-testid="detail-payment-check" className="w-full min-w-0 h-11 rounded-xl text-sm px-2 bg-[#0D786A] hover:bg-[#0A6055]">
            <FileSearch size={16} className="mr-1 shrink-0" /> Payment Check
          </Button>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-900 mb-2 px-1">Work this month</h3>
          <div className="space-y-2">
            {data.entries?.length ? data.entries.map((e) => (
              <div key={e.id} className="df-card p-4 flex items-center justify-between" data-testid={`detail-entry-${e.id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-900">{fmtDateAU(e.date)}</div>
                  <div className="text-xs text-slate-500">Expected {money(e.expected_cents)} · Received {money(e.received_cents)}</div>
                </div>
                <StatusBadge status={e.status} />
              </div>
            )) : <div className="text-sm text-slate-400 px-1">No work recorded this month.</div>}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-900 mb-2 px-1">Payments</h3>
          <div className="space-y-2">
            {data.payments?.length ? data.payments.map((pm) => (
              <div key={pm.id} className="df-card p-4 flex items-center justify-between" data-testid={`detail-payment-${pm.id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-900 font-num">{money(pm.amount_cents)}</div>
                  <div className="text-xs text-slate-500">{fmtDateAU(pm.payment_date)} · {pm.reference || "—"}</div>
                </div>
                <StatusBadge status={pm.status} />
              </div>
            )) : <div className="text-sm text-slate-400 px-1">No payments recorded.</div>}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
